"use client"

import * as React from "react"
import { sanitizeForDisplay } from "../../html"
import { shuffleWithSeed } from "../../shared/shuffle"
import type { DisplayInlineEmbed } from "../../shared/types"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"

// ============================================================================
// Types
// ============================================================================

export interface InlineInteractionProps {
	/** Inline interaction payload to render */
	embed: DisplayInlineEmbed
	/** Current user response (single-select) */
	response?: string
	/** Callback when user selects an answer */
	onAnswerSelect?: (value: string) => void
	/** Whether to disable the interaction */
	disabled?: boolean
	/** Whether submit occurred; used to lock inputs and color selections */
	hasSubmitted?: boolean
	/** Idle palette (before submit) */
	palette?: "default" | "betta" | "cardinal" | "bee" | "owl" | "macaw"
	/** Overall correctness for the selected value (single-select) */
	isCorrect?: boolean
}

// ============================================================================
// Safe HTML Renderer (inline variant)
// ============================================================================

/**
 * Renders sanitized HTML inline. Uses a ref to set innerHTML after sanitization.
 * This avoids React's dangerouslySetInnerHTML prop while maintaining security.
 */
function SafeInlineHTML({ html, className, style }: { html: string; className?: string; style?: React.CSSProperties }) {
	const ref = React.useRef<HTMLSpanElement>(null)

	React.useEffect(() => {
		if (ref.current) {
			ref.current.innerHTML = sanitizeForDisplay(html)
		}
	}, [html])

	return <span ref={ref} className={className} style={style} />
}

// ============================================================================
// Inline Interaction Renderer
// ============================================================================

/**
 * Renders an inline choice interaction as a compact Select control.
 * Mirrors the UX and color rules used by the selects demo and block choice renderer.
 */
export function InlineInteraction({
	embed,
	response,
	onAnswerSelect,
	disabled = false,
	hasSubmitted,
	palette = "macaw",
	isCorrect
}: InlineInteractionProps) {
	const hasValue = typeof response === "string" && response.length > 0
	const neutralTriggerClass = hasValue ? "" : "border-border shadow-[0_3px_0_var(--color-border)] focus-visible:ring-0"

	// Deterministic shuffle for consistent ordering
	const items = React.useMemo(() => {
		return embed.shuffle ? shuffleWithSeed(embed.choices, embed.responseId) : embed.choices
	}, [embed])

	// Feedback-driven palette and invalid state
	const feedbackPalette: typeof palette = hasSubmitted && hasValue ? (isCorrect ? "owl" : "cardinal") : palette
	const ariaInvalid = hasSubmitted && hasValue && isCorrect === false ? true : undefined

	// Selected label HTML for the trigger (mirror menu label, but we control styling here)
	const selectedLabelHtml = React.useMemo(() => {
		if (!hasValue) return ""
		const found = items.find((c) => c.id === response)
		return found?.contentHtml ?? ""
	}, [hasValue, items, response])

	return (
		<Select
			value={hasValue ? response : ""}
			onValueChange={(value) => {
				if (!disabled && onAnswerSelect) onAnswerSelect(value)
			}}
			disabled={disabled || hasSubmitted}
		>
			<SelectTrigger
				size="sm"
				className={neutralTriggerClass}
				palette={hasValue ? feedbackPalette : "default"}
				aria-invalid={ariaInvalid}
			>
				<SelectValue placeholder="Select...">
					{hasValue ? (
						<SafeInlineHTML
							html={selectedLabelHtml}
							className="text-[var(--select-foreground)]"
							style={{ color: "var(--select-foreground)" }}
						/>
					) : null}
				</SelectValue>
			</SelectTrigger>
			<SelectContent position="item-aligned">
				{items.map((c) => (
					<SelectItem key={c.id} value={c.id}>
						<SafeInlineHTML html={c.contentHtml} />
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

export default InlineInteraction
